import { useState } from 'react';
import { format } from 'date-fns';
import { ChevronRight } from 'lucide-react';
import type { PitcherReadiness } from '../../lib/pitcher-readiness';
import { WizardBar, WizardHead, WizardFoot, WizardList } from './WizardChrome';

// Step 1. The recommended arm carries the reasoning; everyone else is one row.
//
// Density rule, agreed with Dan after comparing against GameChanger: only the
// option being suggested shows numbers. Three metrics, not five. Everything else
// lives behind the chevron in the detail sheet, where last outing sits beside the
// season average so a coach can see a regression rather than infer it.

const fmtPct = (v: number | null) => (v == null ? null : `${Math.round(v * 100)}%`);
const fmt2 = (v: number | null) => (v == null ? null : v.toFixed(2));

/** Under 13U reads counting stats; 13U and up reads the rate stats. */
export function isYoungerBand(ageGroup: string): boolean {
  const m = /^(\d+)U$/.exec((ageGroup || '').trim());
  if (m) return Number(m[1]) < 13;
  // T-Ball is younger; HS JV / HS Varsity and anything unparsed are older.
  return /t-?ball/i.test(ageGroup || '');
}

const Radio = ({ on }: { on: boolean }) => (
  <span
    className={`w-5 h-5 rounded-full shrink-0 ${on ? 'border-[6px] border-accent' : 'border-[1.5px] border-track'}`}
  />
);

/** Bottom sheet: last outing beside the season average. */
const PitcherDetail = ({ row, ageGroup, onStart, onCancel }: {
  row: PitcherReadiness;
  ageGroup: string;
  onStart: () => void;
  onCancel: () => void;
}) => {
  const young = isYoungerBand(ageGroup);
  const s = row.season;
  const l = row.lastOuting;

  // Last-outing rate stats are not stored per game, so those cells read "-"
  // rather than borrowing the season figure and implying a measurement.
  const rows: { k: string; last: string; season: string; bad?: boolean }[] = young
    ? [
        { k: 'S%',    last: '–', season: fmtPct(s?.sPct ?? null) ?? '–' },
        { k: 'FPS%',  last: '–', season: fmtPct(s?.fpsPct ?? null) ?? '–', bad: (s?.fpsPct ?? 1) < 0.45 },
        { k: 'WHIP',  last: '–', season: fmt2(s?.whip ?? null) ?? '–' },
        { k: 'K',     last: l?.k != null ? String(l.k) : '–', season: s ? String(s.k) : '–' },
        { k: 'BB',    last: l?.bb != null ? String(l.bb) : '–', season: s ? String(s.bb) : '–' },
        { k: 'R',     last: l?.r != null ? String(l.r) : '–', season: s ? String(s.r) : '–' },
      ]
    : [
        { k: 'K / BB', last: l?.k != null && l?.bb != null ? `${l.k} / ${l.bb}` : '–',
          season: s ? `${s.k} / ${s.bb}` : '–' },
        { k: 'ERA',    last: '–', season: fmt2(s?.era ?? null) ?? '–' },
        { k: 'FPS%',   last: '–', season: fmtPct(s?.fpsPct ?? null) ?? '–', bad: (s?.fpsPct ?? 1) < 0.45 },
        { k: 'WHIP',   last: '–', season: fmt2(s?.whip ?? null) ?? '–' },
      ];

  return (
    <div className="absolute inset-0 z-30 flex flex-col">
      <button className="flex-1 bg-ink/30" onClick={onCancel} aria-label="Dismiss" />
      <div className="bg-surface rounded-t-panel flex flex-col">
        <div className="px-5 pt-5">
          <h3 className="text-[21px] font-bold tracking-[-0.02em] text-ink">{row.player.name}</h3>
          <p className="text-sm text-ink-2 mt-1">
            #{row.player.number} · {row.statusLabel}
            {row.forecastIP != null && `, good for about ${row.forecastIP.toFixed(1)} innings`}
          </p>
        </div>

        {/* 22px clearance either side of the divider so neither column crowds it. */}
        <div className="flex mx-5 mt-4 border-t border-hairline">
          <div className="flex-1 pt-3.5 pr-[22px]">
            <div className="text-[11px] font-bold uppercase tracking-[0.07em] text-ink-2">
              {l ? `Last, ${format(new Date(l.date), 'MMM d')}` : 'Last outing'}
            </div>
            {rows.map(r => (
              <div key={r.k} className="flex justify-between gap-3 py-2 border-b border-hairline last:border-0 text-[14.5px]">
                <span className="text-ink-2">{r.k}</span>
                <span className="font-bold tabular-nums text-ink">{r.last}</span>
              </div>
            ))}
          </div>
          <div className="flex-1 pt-3.5 pl-[22px] border-l border-hairline">
            <div className="text-[11px] font-bold uppercase tracking-[0.07em] text-ink-2">Season</div>
            {rows.map(r => (
              <div key={r.k} className="flex justify-between gap-3 py-2 border-b border-hairline last:border-0 text-[14.5px]">
                <span className="text-ink-2">{r.k}</span>
                <span className={`font-bold tabular-nums ${r.bad ? 'text-danger' : 'text-ink'}`}>{r.season}</span>
              </div>
            ))}
          </div>
        </div>

        {l && (
          <p className="px-5 pt-3 text-[13px] text-ink-2">
            {l.pitches != null && `${l.pitches} pitches`}
            {l.ip != null && `, ${l.ip.toFixed(1)} innings`}
            {l.opponent && ` vs ${l.opponent}`}
          </p>
        )}

        {/* Same footer block as every step, so this button lands at the height
            "Continue" does. No hunting, no relearning. */}
        <WizardFoot
          primaryLabel={`Start ${row.player.name.split(' ')[0]}`}
          onPrimary={onStart}
          secondaryLabel="Cancel"
          onSecondary={onCancel}
        />
      </div>
    </div>
  );
};

export const StepPitcher = ({
  rows, ageGroup, stepLabel, selectedPitcherId, onSelect, onStartAndContinue,
  onBack, onContinue, onSkip,
}: {
  rows: PitcherReadiness[];
  ageGroup: string;
  stepLabel: string;
  selectedPitcherId: string | null;
  onSelect: (id: string | null) => void;
  /** "Start Marcus" is a decision, not a selection: it seats him at P and moves on. */
  onStartAndContinue: (id: string) => void;
  onBack: () => void;
  onContinue: () => void;
  onSkip: () => void;
}) => {
  const [detail, setDetail] = useState<PitcherReadiness | null>(null);
  const recommended = rows.find(r => r.recommended);
  const chosen = rows.find(r => r.player.id === selectedPitcherId);
  // The expanded card shows whoever is actually chosen; the recommendation only
  // fills that slot until the coach picks someone else.
  const hero = chosen ?? recommended;
  const rest = rows.filter(r => r.player.id !== hero?.player.id);

  return (
    <>
      <WizardBar label={stepLabel} onBack={onBack} />
      <WizardHead
        title="Select Starting Pitcher"
        sub={chosen && chosen !== recommended
          ? `${chosen.player.name.split(' ')[0]} is your pick. Tap another name to compare.`
          : recommended?.recommendationReason ?? 'Nobody on the roster has a pitcher rating yet.'}
      />

      <div className="flex-1 overflow-y-auto bg-canvas px-4 pt-4">
        {hero && (
          <button
            onClick={() => onSelect(hero.player.id)}
            className={`w-full text-left bg-surface rounded-panel px-4 py-4 mb-3.5 border-2 ${
              selectedPitcherId === hero.player.id ? 'border-accent' : 'border-hairline'
            }`}
          >
            <span className="flex items-center gap-3">
              <Radio on={selectedPitcherId === hero.player.id} />
              <span className="flex-1 text-[18px] font-bold tracking-[-0.018em] text-ink truncate">
                {hero.player.name}
              </span>
              <span className="text-sm font-semibold text-ink-mute tabular-nums">#{hero.player.number}</span>
            </span>
            <span className="block ml-8 mt-1 text-sm font-semibold text-accent-ink">
              {hero.statusLabel}
              {hero.daysRest != null && hero.status !== 'rest' && ` · ${hero.daysRest} days rest`}
            </span>
            {(hero.forecastIP != null || hero.season) && (
              <span className="block ml-8 mt-3 pt-3 border-t border-hairline">
                {hero.forecastIP != null && (
                  <span className="block text-base font-bold tracking-[-0.01em] text-ink">
                    ~{hero.forecastIP.toFixed(1)} innings
                  </span>
                )}
                {hero.season && (
                  <span className="block text-sm text-ink-2 tabular-nums mt-1">
                    {[
                      hero.season.sPct != null && `S% ${Math.round(hero.season.sPct * 100)}`,
                      hero.season.fpsPct != null && `FPS% ${Math.round(hero.season.fpsPct * 100)}`,
                      hero.season.whip != null && `WHIP ${hero.season.whip.toFixed(2)}`,
                    ].filter(Boolean).join(' · ')}
                  </span>
                )}
              </span>
            )}
          </button>
        )}

        {rest.length > 0 && (
          <WizardList>
            {rest.map((r, i) => (
              <div
                key={r.player.id}
                className={`flex items-center gap-3 px-4 py-3.5 ${i === rest.length - 1 ? '' : 'border-b border-hairline'} ${r.status === 'rest' ? 'opacity-55' : ''}`}
              >
                <button
                  onClick={() => r.status !== 'rest' && onSelect(r.player.id)}
                  disabled={r.status === 'rest'}
                  className="flex items-center gap-3 flex-1 min-w-0 text-left"
                >
                  {r.status === 'rest'
                    ? <span className="w-5 shrink-0 text-center text-ink-mute text-sm">✕</span>
                    : <Radio on={selectedPitcherId === r.player.id} />}
                  <span className={`flex-1 min-w-0 text-base font-semibold tracking-[-0.012em] truncate ${r.status === 'rest' ? 'text-ink-mute' : 'text-ink'}`}>
                    {r.player.name}
                  </span>
                  <span className="text-sm text-ink-2 tabular-nums shrink-0">{r.statusLabel}</span>
                </button>
                <button
                  onClick={() => setDetail(r)}
                  aria-label={`${r.player.name} details`}
                  className="shrink-0 -mr-1 p-1"
                >
                  <ChevronRight className="w-5 h-5 text-ink-mute" />
                </button>
              </div>
            ))}
          </WizardList>
        )}
        <div className="h-4" />
      </div>

      <WizardFoot
        primaryLabel="Continue"
        onPrimary={onContinue}
        secondaryLabel="Skip this step"
        onSecondary={onSkip}
      />

      {detail && (
        <PitcherDetail
          row={detail}
          ageGroup={ageGroup}
          onStart={() => { const id = detail.player.id; setDetail(null); onStartAndContinue(id); }}
          onCancel={() => setDetail(null)}
        />
      )}
    </>
  );
};
